/**
 * Equipment Drawer
 * Create / Edit equipment records.
 */

import { X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  SearchableDropdown,
  TextArea,
} from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
} from '../../services/equipmentApi';
import type {
  Equipment,
  EquipmentCategory,
  EquipmentCondition,
  EquipmentOwnership,
  EquipmentStatus,
  CreateEquipmentRequest,
  UpdateEquipmentRequest,
} from '../../types/equipment';

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_OPTIONS = [
  { value: 'heavy', label: 'Heavy Equipment' },
  { value: 'light', label: 'Light Equipment' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'tool', label: 'Tool' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' },
];

const OWNERSHIP_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'rented', label: 'Rented' },
  { value: 'leased', label: 'Leased' },
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'in_maintenance', label: 'In Maintenance' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'retired', label: 'Retired' },
];

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'needs_attention', label: 'Needs Attention' },
  { value: 'out_of_service', label: 'Out of Service' },
];

// ============================================================================
// Props
// ============================================================================

interface EquipmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  equipment?: Equipment | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Component
// ============================================================================

const EquipmentDrawer: React.FC<EquipmentDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  equipment,
  mode,
}) => {
  const toast = useToast();

  // Basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<EquipmentCategory>('other');
  const [type, setType] = useState('');

  // Specs
  const [make, setMake] = useState('');
  const [modelName, setModelName] = useState('');
  const [year, setYear] = useState<number | undefined>(undefined);
  const [serialNumber, setSerialNumber] = useState('');

  // Ownership
  const [ownership, setOwnership] = useState<EquipmentOwnership>('owned');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState<number | undefined>(undefined);

  // Rental info (shown when ownership = rented/leased)
  const [rentalVendorName, setRentalVendorName] = useState('');
  const [rentalStartDate, setRentalStartDate] = useState('');
  const [rentalEndDate, setRentalEndDate] = useState('');
  const [rentalDailyRate, setRentalDailyRate] = useState<number | undefined>(undefined);

  // Status & condition
  const [status, setStatus] = useState<EquipmentStatus>('available');
  const [condition, setCondition] = useState<EquipmentCondition | ''>('');

  // Meters & rates
  const [hourMeter, setHourMeter] = useState<number | undefined>(undefined);
  const [odometerReading, setOdometerReading] = useState<number | undefined>(undefined);
  const [hourlyRate, setHourlyRate] = useState<number | undefined>(undefined);

  // Maintenance dates
  const [lastMaintenanceDate, setLastMaintenanceDate] = useState('');
  const [nextMaintenanceDate, setNextMaintenanceDate] = useState('');

  // Notes
  const [notes, setNotes] = useState('');

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createEquipment, { isLoading: isCreating }] = useCreateEquipmentMutation();
  const [updateEquipment, { isLoading: isUpdating }] = useUpdateEquipmentMutation();

  const isSubmitting = isCreating || isUpdating;
  const showRentalInfo = ownership === 'rented' || ownership === 'leased';

  // ============================================================================
  // Reset / populate form
  // ============================================================================

  useEffect(() => {
    if (isOpen && mode === 'edit' && equipment) {
      setName(equipment.name || '');
      setDescription(equipment.description || '');
      setCategory(equipment.category);
      setType(equipment.type || '');
      setMake(equipment.make || '');
      setModelName(equipment.modelName || '');
      setYear(equipment.year);
      setSerialNumber(equipment.serialNumber || '');
      setOwnership(equipment.ownership);
      setPurchaseDate(equipment.purchaseDate ? equipment.purchaseDate.split('T')[0] : '');
      setPurchasePrice(equipment.purchasePrice);
      setStatus(
        (equipment.status === 'assigned'
          ? 'available'
          : equipment.status) as EquipmentStatus
      );
      setCondition(equipment.condition || '');
      setHourMeter(equipment.hourMeter);
      setOdometerReading(equipment.odometerReading);
      setHourlyRate(equipment.hourlyRate);
      setLastMaintenanceDate(
        equipment.lastMaintenanceDate ? equipment.lastMaintenanceDate.split('T')[0] : ''
      );
      setNextMaintenanceDate(
        equipment.nextMaintenanceDate ? equipment.nextMaintenanceDate.split('T')[0] : ''
      );
      setNotes(equipment.notes || '');
      // Rental info
      if (equipment.rentalInfo) {
        setRentalVendorName(equipment.rentalInfo.vendorName || '');
        setRentalStartDate(
          equipment.rentalInfo.startDate ? equipment.rentalInfo.startDate.split('T')[0] : ''
        );
        setRentalEndDate(
          equipment.rentalInfo.endDate ? equipment.rentalInfo.endDate.split('T')[0] : ''
        );
        setRentalDailyRate(equipment.rentalInfo.dailyRate);
      } else {
        setRentalVendorName('');
        setRentalStartDate('');
        setRentalEndDate('');
        setRentalDailyRate(undefined);
      }
    } else if (isOpen && mode === 'create') {
      setName('');
      setDescription('');
      setCategory('other');
      setType('');
      setMake('');
      setModelName('');
      setYear(undefined);
      setSerialNumber('');
      setOwnership('owned');
      setPurchaseDate('');
      setPurchasePrice(undefined);
      setRentalVendorName('');
      setRentalStartDate('');
      setRentalEndDate('');
      setRentalDailyRate(undefined);
      setStatus('available');
      setCondition('');
      setHourMeter(undefined);
      setOdometerReading(undefined);
      setHourlyRate(undefined);
      setLastMaintenanceDate('');
      setNextMaintenanceDate('');
      setNotes('');
      setErrors({});
    }
  }, [isOpen, mode, equipment]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Equipment name is required';
    if (!type.trim()) errs.type = 'Equipment type is required';
    if (showRentalInfo && !rentalVendorName.trim()) {
      errs.rentalVendorName = 'Vendor name is required for rented/leased equipment';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [name, type, showRentalInfo, rentalVendorName]);

  // ============================================================================
  // Submit
  // ============================================================================

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    const rentalInfo =
      showRentalInfo && rentalVendorName.trim()
        ? {
            vendorName: rentalVendorName.trim(),
            startDate: rentalStartDate || undefined,
            endDate: rentalEndDate || undefined,
            dailyRate: rentalDailyRate,
          }
        : undefined;

    try {
      if (mode === 'create') {
        const payload: CreateEquipmentRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          type: type.trim(),
          make: make.trim() || undefined,
          modelName: modelName.trim() || undefined,
          year: year || undefined,
          serialNumber: serialNumber.trim() || undefined,
          ownership,
          purchaseDate: purchaseDate || undefined,
          purchasePrice: purchasePrice,
          rentalInfo,
          status,
          hourMeter: hourMeter,
          odometerReading: odometerReading,
          hourlyRate: hourlyRate,
          condition: condition || undefined,
          lastMaintenanceDate: lastMaintenanceDate || undefined,
          nextMaintenanceDate: nextMaintenanceDate || undefined,
          notes: notes.trim() || undefined,
        };

        await createEquipment({ projectId, data: payload }).unwrap();
        toast.success('Equipment created successfully');
      } else if (equipment) {
        const payload: UpdateEquipmentRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          category,
          type: type.trim(),
          make: make.trim() || undefined,
          modelName: modelName.trim() || undefined,
          year: year || undefined,
          serialNumber: serialNumber.trim() || undefined,
          ownership,
          purchaseDate: purchaseDate || undefined,
          purchasePrice: purchasePrice,
          rentalInfo: rentalInfo ?? null,
          status: status as UpdateEquipmentRequest['status'],
          hourMeter: hourMeter,
          odometerReading: odometerReading,
          hourlyRate: hourlyRate,
          condition: condition || undefined,
          lastMaintenanceDate: lastMaintenanceDate || undefined,
          nextMaintenanceDate: nextMaintenanceDate || undefined,
          notes: notes.trim() || undefined,
        };

        await updateEquipment({
          projectId,
          equipmentId: equipment._id,
          data: payload,
        }).unwrap();
        toast.success('Equipment updated successfully');
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : mode === 'create'
          ? 'Failed to create equipment'
          : 'Failed to update equipment';
      toast.error(message);
    }
  }, [
    validate,
    mode,
    equipment,
    projectId,
    name,
    description,
    category,
    type,
    make,
    modelName,
    year,
    serialNumber,
    ownership,
    purchaseDate,
    purchasePrice,
    showRentalInfo,
    rentalVendorName,
    rentalStartDate,
    rentalEndDate,
    rentalDailyRate,
    status,
    condition,
    hourMeter,
    odometerReading,
    hourlyRate,
    lastMaintenanceDate,
    nextMaintenanceDate,
    notes,
    createEquipment,
    updateEquipment,
    onClose,
    toast,
  ]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer — large because of multiple sections */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'Add Equipment' : 'Edit Equipment'}
              </h2>
              {equipment && (
                <p className="text-xs text-gray-500 mt-0.5">{equipment.equipmentNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="space-y-6">

              {/* ============================================================
                  SECTION: Basic Info
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Equipment Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Category
                    </label>
                    <SearchableDropdown
                      options={CATEGORY_OPTIONS}
                      value={category}
                      onChange={val => setCategory(val as EquipmentCategory)}
                      placeholder="Select category"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={type}
                      onChange={e => setType(e.target.value)}
                      placeholder="e.g. Excavator, Forklift, Crane"
                    />
                    {errors.type && (
                      <p className="text-xs text-red-500 mt-1">{errors.type}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Equipment name"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <TextArea
                      value={description}
                      onChange={setDescription}
                      placeholder="Optional description"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Specifications
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Specifications
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Make</label>
                    <Input
                      value={make}
                      onChange={e => setMake(e.target.value)}
                      placeholder="e.g. Caterpillar, Komatsu"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Model</label>
                    <Input
                      value={modelName}
                      onChange={e => setModelName(e.target.value)}
                      placeholder="e.g. 320 GC"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Year</label>
                    <NumberInput
                      value={year}
                      onChange={setYear}
                      placeholder="e.g. 2022"
                      min={1900}
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Serial Number</label>
                    <Input
                      value={serialNumber}
                      onChange={e => setSerialNumber(e.target.value)}
                      placeholder="Serial / VIN number"
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Ownership & Cost
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Ownership
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ownership</label>
                    <Dropdown
                      options={OWNERSHIP_OPTIONS}
                      value={ownership}
                      onChange={val => setOwnership(val as EquipmentOwnership)}
                      placeholder="Select ownership"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Purchase Date</label>
                    <DatePicker
                      value={purchaseDate}
                      onChange={setPurchaseDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Purchase Price</label>
                    <CurrencyInput
                      value={purchasePrice}
                      onChange={setPurchasePrice}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Hourly Rate (Internal)</label>
                    <CurrencyInput
                      value={hourlyRate}
                      onChange={setHourlyRate}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Rental info — shown conditionally */}
                {showRentalInfo && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3">
                      Rental / Lease Details
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">
                          Vendor Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          value={rentalVendorName}
                          onChange={e => setRentalVendorName(e.target.value)}
                          placeholder="Rental company name"
                        />
                        {errors.rentalVendorName && (
                          <p className="text-xs text-red-500 mt-1">{errors.rentalVendorName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                        <DatePicker
                          value={rentalStartDate}
                          onChange={setRentalStartDate}
                          placeholder="Rental start"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">End Date</label>
                        <DatePicker
                          value={rentalEndDate}
                          onChange={setRentalEndDate}
                          placeholder="Rental end"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Daily Rate</label>
                        <CurrencyInput
                          value={rentalDailyRate}
                          onChange={setRentalDailyRate}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ============================================================
                  SECTION: Status & Condition
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Status & Condition
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Status</label>
                    <Dropdown
                      options={STATUS_OPTIONS}
                      value={status}
                      onChange={val => setStatus(val as EquipmentStatus)}
                      placeholder="Select status"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Condition</label>
                    <Dropdown
                      options={CONDITION_OPTIONS}
                      value={condition}
                      onChange={val => setCondition(val as EquipmentCondition)}
                      placeholder="Select condition"
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Meters
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Meter Readings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Hour Meter (hrs)</label>
                    <NumberInput
                      value={hourMeter}
                      onChange={setHourMeter}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Odometer (miles/km)</label>
                    <NumberInput
                      value={odometerReading}
                      onChange={setOdometerReading}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Maintenance
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Maintenance
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Last Maintenance Date</label>
                    <DatePicker
                      value={lastMaintenanceDate}
                      onChange={setLastMaintenanceDate}
                      placeholder="Select date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Next Maintenance Due</label>
                    <DatePicker
                      value={nextMaintenanceDate}
                      onChange={setNextMaintenanceDate}
                      placeholder="Select date"
                    />
                  </div>
                </div>
              </div>

              {/* ============================================================
                  SECTION: Notes
              ============================================================ */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Notes
                </h3>
                <TextArea
                  value={notes}
                  onChange={setNotes}
                  placeholder="Additional notes about this equipment..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? mode === 'create'
                  ? 'Adding...'
                  : 'Saving...'
                : mode === 'create'
                ? 'Add Equipment'
                : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default EquipmentDrawer;
